/**
 * Casper-Style Add-On Bundle — US site (LIVE, not an A/B test)
 * ───────────────────────────────────────────────────────────
 * Ported & expanded from main-ca `assets/onsite-test-bumper-kit-addon.js`.
 *
 * Renders a dual-card bundle directly below the Assembly Options block
 * (#bundle-options) on Front Bumper product pages:
 *   • 240-Piece Bumper Clip Kit  — +$20.00  (fixed universal variant)
 *   • Touch-Up Bottle Essential Kit — +$19.99 (resolved by make + paint selection)
 *
 * One master toggle (derived state of the two item toggles) + a per-item
 * toggle each. Self-contained: keeps its own total line and never writes to
 * kit-bundle.liquid's shared #bundle-total-* / #price-display-a2c elements.
 *
 * Config is injected by snippets/casper-addon-bundle.liquid as window.__PARTIFY_BUNDLE.
 */
(function () {
	var CFG = window.__PARTIFY_BUNDLE || {};
	var LOG = "[casper-bundle]";

	function log() {
		try {
			console.log.apply(console, [LOG].concat([].slice.call(arguments)));
		} catch (e) {}
	}

	// Which add-ons can appear on THIS product:
	//  • Clip kit — only on bumpers tagged 'Bundle-Bumper-Clips' (fitment control, like CA).
	//  • Touch-up paint kit — only when a matching product exists for the make.
	var FIXED_STEEL_BUMPER = hasFixedSteelBumper();
	var CLIP_ON =
		!!CFG.clipEligible &&
		!!(CFG.kitVariantId || CFG.kitProductHandle) &&
		!FIXED_STEEL_BUMPER;
	var PAINT_ON = !!CFG.paintBottleHandle;

	if (!CLIP_ON && !PAINT_ON) {
		log("neither add-on eligible for this product — not rendering");
		return;
	}

	// ── Money helpers ──────────────────────────────────────────────────────
	function formatMoney(cents) {
		var n = Number(cents) || 0;
		try {
			if (window.Shopify && typeof Shopify.formatMoney === "function") {
				return Shopify.formatMoney(Math.round(n));
			}
		} catch (e) {}
		return "$" + (n / 100).toFixed(2);
	}
	function parseMoneyToCents(text) {
		if (!text) return null;
		var num = parseFloat(String(text).replace(/[^0-9.]/g, ""));
		return isNaN(num) ? null : Math.round(num * 100);
	}

	// ── State (master is DERIVED, never an independent variable) ─────────────
	var state = {
		clip: {
			selected: false,
			variantId: CFG.kitVariantId || null,
			priceCents: Number(CFG.kitPriceCents) || 2000,
			available: true, // verified against /products/{handle}.js on mount
			inCart: false,
			image: null,
		},
		paint: {
			selected: false,
			priceCents: Number(CFG.paintPriceCents) || 1999,
			code: null, // paint code copied from the PDP paint selector
			variantId: null, // resolved touch-up paint kit variant
			resolved: false, // whether we attempted resolution for the current code
			available: !!CFG.paintBottleHandle, // can a paint product even be resolved?
			inCart: false,
		},
	};

	// Mutable visibility flags track product eligibility; cart state disables items without hiding them.
	let clipVisible = CLIP_ON && !isSteelBumperSelected();
	let paintVisible = PAINT_ON;

	function hasFixedSteelBumper() {
		const fitmentCard = document.querySelector("[data-pfc-keystone]");
		const details = fitmentCard
			? fitmentCard.getAttribute("data-pfc-keystone")
			: "";
		return String(details || "")
			.split(/\u2022|\u00b7|<br\s*\/?>|\n/i)
			.some(function (detail) {
				const value = detail
					.replace(/<[^>]*>/g, "")
					.trim()
					.toLowerCase();
				return (
					(value.indexOf("made of") !== -1 && value.indexOf("steel") !== -1) ||
					(value.indexOf("bumper material") !== -1 &&
						value.indexOf("steel") !== -1) ||
					value === "steel bumper" ||
					value === "steel"
				);
			});
	}

	function isSteelBumperSelected() {
		if (FIXED_STEEL_BUMPER) return true;
		const selectedMaterial = document.querySelector(
			'input[name="properties[Bumper Material]"]:checked',
		);
		return (
			!!selectedMaterial &&
			String(selectedMaterial.value).trim().toLowerCase() === "steel"
		);
	}

	function masterOn() {
		return (
			(clipVisible && state.clip.selected) ||
			(paintVisible && state.paint.selected)
		);
	}

	function hasSelectableItems() {
		return (
			(clipVisible && !state.clip.inCart && state.clip.available !== false) ||
			(paintVisible && !state.paint.inCart && paintReady())
		);
	}

	// ── DOM refs (filled after render) ───────────────────────────────────────
	function getPaintLineProperties() {
		const activeVin = getActiveVin();
		return Object.assign(
			{
				"Paint Code": isMatchPaintByVinCode(state.paint.code)
					? "Match by VIN"
					: state.paint.code,
			},
			activeVin ? { VIN: activeVin } : {},
			{ Bundled: "Casper add-on bundle" },
		);
	}

	function normalizePaintIdentity(value) {
		if (isMatchPaintByVinCode(value)) return "MATCH BY VIN";
		return (
			_cleanCode(value) ||
			String(value || "")
				.trim()
				.toUpperCase()
		);
	}

	function normalizeVinIdentity(value) {
		const vin = String(value || "")
			.trim()
			.toUpperCase();
		return /^[A-HJ-NPR-Z0-9]{17}$/.test(vin) ? vin : "";
	}

	function getLinePaintCode(properties) {
		return (
			(properties &&
				(properties["Paint Code"] || properties["Touch-Up Color"])) ||
			""
		);
	}

	function samePaintConfiguration(cartItem, selectedItem) {
		if (!cartItem || !selectedItem) return false;
		if (String(cartItem.variant_id) !== String(selectedItem.id)) return false;

		const cartProps = cartItem.properties || {};
		const selectedProps = selectedItem.properties || {};
		if (
			normalizePaintIdentity(getLinePaintCode(cartProps)) !==
			normalizePaintIdentity(getLinePaintCode(selectedProps))
		) {
			return false;
		}

		const cartVin = normalizeVinIdentity(cartProps.VIN);
		const selectedVin = normalizeVinIdentity(selectedProps.VIN);
		return selectedVin ? cartVin === selectedVin : !cartVin;
	}

	var els = {};

	// ── Cart-aware filtering ──────────────────────────────────────────────────
	// Clip kit: keyed on product handle (universal SKU GA1061240); fallback to variant ID.
	// Touch-up kit: keyed on "Casper add-on bundle" property + is NOT the clip kit product.
	// Add-ons already in cart stay visible but disabled, so eligibility is still obvious.
	var cartFilterGeneration = 0;
	function invalidateCartFilter() {
		return ++cartFilterGeneration;
	}

	function cartFilterDialogOpen() {
		const dialog = document.querySelector("[data-casper-package-dialog]");
		return !!(dialog && dialog.open);
	}

	function refreshCartFilter(generation) {
		if (generation !== cartFilterGeneration) return;
		const modalWasOpen = cartFilterDialogOpen();
		const paintGeneration = _paintResolveToken;
		return checkCartForAddOns().then(function (result) {
			if (
				generation !== cartFilterGeneration ||
				paintGeneration !== _paintResolveToken ||
				modalWasOpen !== cartFilterDialogOpen()
			) return;
			applyCartFilter(result.clipInCart, result.paintInCart);
		});
	}

	function checkCartForAddOns() {
		return fetch("/cart.js", {
			cache: "reload",
			credentials: "same-origin",
			headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
		})
			.then(function (r) {
				return r.ok ? r.json() : null;
			})
			.then(function (cart) {
				if (!cart || !cart.items) {
					console.log("wool [casper-bundle] checkCartForAddOns: empty cart");
					return { clipInCart: false, paintInCart: false };
				}
				const clipVariantId = CFG.kitVariantId
					? String(CFG.kitVariantId)
					: null;
				const clipInCart = cart.items.some(function (item) {
					if (CFG.kitProductHandle && item.handle === CFG.kitProductHandle)
						return true;
					if (clipVariantId && String(item.variant_id) === clipVariantId)
						return true;
					return false;
				});
				const paintInCart = cart.items.some(function (item) {
					if (!state.paint.variantId || !state.paint.code) return false;
					return samePaintConfiguration(item, {
						id: String(state.paint.variantId),
						properties: getPaintLineProperties(),
					});
				});
				console.log("wool [casper-bundle] checkCartForAddOns", {
					itemCount: cart.items.length,
					handles: cart.items.map(function (i) {
						return i.handle;
					}),
					clipInCart: clipInCart,
					paintInCart: paintInCart,
					kitHandle: CFG.kitProductHandle,
					paintHandle: CFG.paintBottleHandle,
				});
				return { clipInCart: clipInCart, paintInCart: paintInCart };
			})
			.catch(function (e) {
				console.log("wool [casper-bundle] checkCartForAddOns error", e);
				return { clipInCart: false, paintInCart: false };
			});
	}

	function applyCartFilter(clipInCart, paintInCart) {
		console.log("wool [casper-bundle] applyCartFilter", {
			clipInCart: clipInCart,
			paintInCart: paintInCart,
			wrapExists: !!els.wrap,
			wrapInDoc: !!(els.wrap && document.contains(els.wrap)),
		});
		if (!els.wrap || !document.contains(els.wrap)) {
			console.log(
				"wool [casper-bundle] applyCartFilter: guard tripped, skipping",
			);
			return;
		}
		const wasPaintVisible = paintVisible;
		state.clip.inCart = !!clipInCart;
		state.paint.inCart = !!paintInCart;
		clipVisible = CLIP_ON && !isSteelBumperSelected();
		paintVisible = PAINT_ON;

		// Deselect now-hidden or already-in-cart items before any early return so state never drifts.
		if ((!clipVisible || state.clip.inCart) && state.clip.selected) {
			state.clip.selected = false;
			if (els.clipInput) els.clipInput.checked = false;
		}
		if ((!paintVisible || state.paint.inCart) && state.paint.selected) {
			state.paint.selected = false;
			if (els.paintInput) els.paintInput.checked = false;
		}

		if (!clipVisible && !paintVisible) {
			els.wrap.hidden = true;
			return;
		}
		els.wrap.hidden = false;

		const clipCard = els.wrap.querySelector('[data-item="clip"]');
		const paintCard = els.wrap.querySelector('[data-item="paint"]');
		const divider = els.wrap.querySelector(".casper-bundle__plus");

		if (clipCard) clipCard.hidden = !clipVisible;
		if (paintCard) paintCard.hidden = !paintVisible;
		if (divider) divider.hidden = !(clipVisible && paintVisible);

		reflectToggles();
		updateAvailabilityUI();
		updateTotals();
		updatePaintWarning();

		// When the paint card transitions from hidden → visible (paint was in cart on load and
		// user just removed it), resolution and VIN polling were never started — kick them off now.
		if (paintVisible && !wasPaintVisible) {
			resolvePaint();
			startVinPolling();
		}
	}

	// VIN polling handle — allows startVinPolling() to be safely called multiple times.
	let _vinPoll = null;
	function startVinPolling() {
		if (_vinPoll) return;
		let _lastSeenCode = state.paint.code;
		let _codePolls = 0;
		_vinPoll = setInterval(function () {
			_codePolls++;
			const c = getCurrentPaintCode();
			if (c !== _lastSeenCode) {
				_lastSeenCode = c;
				state.paint.resolved = false;
				resolvePaint();
			}
			if (_codePolls >= 20) {
				clearInterval(_vinPoll);
				_vinPoll = null;
			}
		}, 500);
	}

	function bindCartChangeListener() {
		// Debounce: cart:refresh and cart:update often fire together for the same mutation.
		let _debounceTimer = null;
		function onCartChange() {
			// Invalidate immediately: an older response may finish during the debounce.
			const generation = invalidateCartFilter();
			if (_debounceTimer) clearTimeout(_debounceTimer);
			_debounceTimer = setTimeout(function () {
				_debounceTimer = null;
				refreshCartFilter(generation);
			}, 80);
		}
		document.addEventListener("cart:refresh", onCartChange);
		document.addEventListener("cart:update", onCartChange);
		window.addEventListener("cartcount:update", onCartChange);
		// Re-check when the browser restores this page from the back-forward cache (bfcache).
		// Without this, navigating away before cart:refresh fires leaves the page in its
		// pre-filter state, and bfcache snapshots that — so the next back-nav shows stale cards.
		window.addEventListener("pageshow", function (e) {
			if (e.persisted) onCartChange();
		});
		// A pre-modal response must not cross an open/cancel/reopen selection session.
		if (window.MutationObserver) {
			new MutationObserver(function (records) {
				if (records.some(function (record) {
					return record.target.matches("[data-casper-package-dialog]");
				})) invalidateCartFilter();
			}).observe(document.body, { subtree: true, attributes: true, attributeFilter: ["open"] });
		}
	}

	// ── Paint code helpers (reused/extended from CA getCurrentPaintCode) ──────
	function _cleanCode(raw) {
		if (!raw) return null;
		var m = String(raw)
			.trim()
			.match(/^([A-Z0-9][A-Z0-9.\-\/]{1,8})\b/i);
		var code = m ? m[1].trim().toUpperCase() : "";
		// Exclude non-codes: VIN/license prompts, and the "Unpainted" option (which must
		// never be treated as a paint code — that produced bogus "UNPAINTED" line items).
		if (!code || code === "VIN" || code === "BY" || code === "FIND")
			return null;
		if (code === "MATCH" || code === "SELECT") return null; // "Match by VIN" / "Select Paint Code" prompts
		if (/^UNPAINT/.test(code) || /^SINPINT/.test(code)) return null;
		return code;
	}
	function isMatchPaintByVinText(value) {
		return /match\s+paint\s+by\s+vin|match\s+by\s+vin|pintura\s+de\s+combinaci[o\u00f3]n\s+por\s+vin/i.test(
			String(value || ""),
		);
	}
	function isPaintMatchSentinelText(value) {
		const text = String(value || "");
		return isMatchPaintByVinText(text) ||
			/match\s+(paint\s+)?(code\s+)?by\s+license(\s+plate)?/i.test(text);
	}
	function isMatchPaintByVinSku(value) {
		return /(^|[._-])VIN([._-]|$)/i.test(String(value || ""));
	}
	function hasVinContext() {
		try {
			var terms = JSON.parse(localStorage.getItem("searchTerms") || "[]");
			var first = Array.isArray(terms) && terms[0] ? terms[0] : {};
			return !!(first.vin || first.plate || first.decodedByBumper);
		} catch (e) {
			return false;
		}
	}
	function isTwoTonePaintModeActive() {
		if (document.documentElement.dataset.partifyTwoTonePaint === "true") return true;
		if (typeof twoToneSelected === "boolean") return twoToneSelected;
		return false;
	}
	function selectedVinPaintMode() {
		const selected = document.querySelector('input[name="select_paint_option"]:checked');
		if (!selected) return false;
		return (
			selected.id === "checkbox-get-paint-code-with-vin" ||
			selected.id === "checkbox-get-paint-code-with-license" ||
			selected.id === "checkbox-paint-code-local-storage"
		);
	}
	function getSelectedDropdownPaintCode(allowPaintMatchSentinel) {
		const sel = document.getElementById("variant-selector");
		if (!sel) return null;
		const opt = sel.options[sel.selectedIndex];
		if (!opt || opt.hidden || opt.disabled) return null;
		const optText = opt.textContent || opt.innerText || "";
		const optTitle = (opt.dataset && opt.dataset.variantTitle) || "";
		const optSku = (opt.dataset && opt.dataset.variantSku) || "";
		if (isPaintMatchSentinelText(optText) || isPaintMatchSentinelText(optTitle) || isMatchPaintByVinSku(optSku)) {
			return allowPaintMatchSentinel ? "Match Paint by VIN" : null;
		}
		const fromData =
			(opt.dataset && (opt.dataset.paintCode || opt.dataset.code)) || "";
		return _cleanCode(fromData) || _cleanCode(optText);
	}
	function getActiveVin() {
		const cleanVin = function (value) {
			const vin = String(value || "")
				.trim()
				.toUpperCase();
			return /^[A-HJ-NPR-Z0-9]{17}$/.test(vin) ? vin : "";
		};

		if (
			window.PartifyVinCartGuard &&
			typeof window.PartifyVinCartGuard.getAcceptedVehicleVin === "function"
		) {
			const acceptedVin = window.PartifyVinCartGuard.getAcceptedVehicleVin();
			if (acceptedVin) return acceptedVin;
		}

		const getterVin = cleanVin(
			(function () {
				try {
					const getter = window.getSearchTerms || null;
					const terms = typeof getter === "function" ? getter() : [];
					return (Array.isArray(terms) && terms[0] && terms[0].vin) || "";
				} catch (e) {
					return "";
				}
			})(),
		);
		if (getterVin) return getterVin;

		try {
			const terms = JSON.parse(localStorage.getItem("searchTerms") || "[]");
			return cleanVin((Array.isArray(terms) && terms[0] && terms[0].vin) || "");
		} catch (e) {
			return "";
		}
	}
	function getActiveSearchTerm() {
		try {
			const getter = window.getSearchTerms || null;
			const terms =
				typeof getter === "function"
					? getter()
					: JSON.parse(localStorage.getItem("searchTerms") || "[]");
			return Array.isArray(terms) && terms[0] ? terms[0] : null;
		} catch (e) {
			return null;
		}
	}
	function isCurrentPaintVinDerivedExactCode(code, vin) {
		if (!code || !vin || isMatchPaintByVinCode(code)) return false;

		const storedPaintRadio = document.getElementById(
			"checkbox-paint-code-local-storage",
		);
		if (
			!storedPaintRadio ||
			!storedPaintRadio.checked ||
			storedPaintRadio.disabled
		)
			return false;

		const activeTerm = getActiveSearchTerm();
		const termVin = String((activeTerm && activeTerm.vin) || "")
			.trim()
			.toUpperCase();
		if (!/^[A-HJ-NPR-Z0-9]{17}$/.test(termVin) || termVin !== vin) return false;

		const termPaintCode = _cleanCode(
			(activeTerm && activeTerm.paintCode) || "",
		);
		return !!termPaintCode && termPaintCode === _cleanCode(code);
	}
	function isMatchPaintByVinCode(value) {
		var raw = String(value || "").trim();
		var upper = raw.toUpperCase();
		return upper === ".VIN" || upper === "VIN" || isMatchPaintByVinText(raw);
	}
	function matchPaintByVinCandidates(code) {
		return isMatchPaintByVinCode(code)
			? ["Match Paint by VIN", "Match by VIN", ".VIN", "VIN"]
			: [code];
	}
	function getCurrentPaintCode() {
		// 0) If the shopper picked "Unpainted", there is no paint code — and no kit to
		// match. (The #variant-selector may still hold a stale code, so check this first.)
		var unpainted = document.getElementById("checkbox-select-unpainted");
		if (unpainted && unpainted.checked) return null;
		if (isTwoTonePaintModeActive()) {
			return getSelectedDropdownPaintCode(false);
		}
		if (CFG.paintMatchByVinOnly && hasVinContext() && selectedVinPaintMode()) {
			return "Match Paint by VIN";
		}
		// 1) The paint-code dropdown, if present and populated.
		const selectedDropdownCode = getSelectedDropdownPaintCode(true);
		if (selectedDropdownCode) return selectedDropdownCode;
		// 2) The selected "Painted XXX" radio label (e.g. VIN-determined code).
		var radio = document.querySelector(
			'input[name="select_paint_option"]:checked',
		);
		if (radio && radio.id !== "checkbox-select-unpainted") {
			var lbl =
				radio.closest("label") ||
				document.querySelector('label[for="' + radio.id + '"]');
			var txt = lbl ? lbl.textContent : "";
			if (isPaintMatchSentinelText(txt)) return "Match Paint by VIN";
			// Strip a leading "Painted"/"Pintado" word, then read the code.
			txt = txt.replace(/^\s*(painted|pintado)\s+/i, "");
			var c2 = _cleanCode(txt);
			if (c2) return c2;
		}
		// 3) Global dataVariantTitle set by the paint options snippet on selection.
		if (window.dataVariantTitle) {
			var title = String(window.dataVariantTitle);
			if (isMatchPaintByVinText(title)) return "Match Paint by VIN";
			var c3 = _cleanCode(title.replace(/^\s*(painted|pintado)\s+/i, ""));
			if (c3) return c3;
		}
		return null;
	}

	// ── Touch-up bottle resolution (per make + code, 1/2 oz = $17.99) ────────
	var _bottleProductPromise = null;
	function fetchBottleProduct() {
		if (!CFG.paintBottleHandle) return Promise.resolve(null);
		if (_bottleProductPromise) return _bottleProductPromise;
		_bottleProductPromise = fetch("/products/" + CFG.paintBottleHandle + ".js")
			.then(function (r) {
				if (!r.ok) throw new Error("bottle product " + r.status);
				return r.json();
			})
			.catch(function (e) {
				log("bottle product fetch failed", CFG.paintBottleHandle, e);
				return null;
			});
		return _bottleProductPromise;
	}

	// Resolve the EXACT bottle variant by option value (Paint Code + Size) via the
	// Storefront API. This is position-independent, so it works for paint codes past
	// Shopify's 250-variant cap on /products/{handle}.js and Liquid `for variant` loops
	// (e.g. Chrysler/Subaru have 250+ codes; "K1X" sorts past #250 and is absent from
	// the Ajax list). Without this we'd silently fall back to the wrong color.
	function resolveVariantByCode(code) {
		if (!code || !CFG.paintBottleHandle || !CFG.storefrontToken)
			return Promise.resolve(null);
		var candidates = matchPaintByVinCandidates(code);
		var query =
			"query($handle:String!,$opts:[SelectedOptionInput!]!){" +
			" product(handle:$handle){ variantBySelectedOptions(selectedOptions:$opts){" +
			" id title availableForSale price{amount} } } }";
		var tryCandidate = function (idx) {
			var candidate = candidates[idx];
			if (!candidate) return Promise.resolve(null);
			var selectedOptions = [{ name: "Paint Code", value: candidate }];
			if (CFG.paintRequiresSizeOption !== false) {
				selectedOptions.push({ name: "Size", value: "1/2 oz" });
			}
			return fetch("/api/2024-01/graphql.json", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					"X-Shopify-Storefront-Access-Token": CFG.storefrontToken,
				},
				body: JSON.stringify({
					query: query,
					variables: {
						handle: CFG.paintBottleHandle,
						opts: selectedOptions,
					},
				}),
			})
				.then(function (r) {
					return r.ok ? r.json() : null;
				})
				.then(function (j) {
					var v =
						j &&
						j.data &&
						j.data.product &&
						j.data.product.variantBySelectedOptions;
					if (!v || !v.id) return tryCandidate(idx + 1);
					return {
						id: String(v.id).split("/").pop(),
						priceCents:
							Math.round(
								parseFloat((v.price && v.price.amount) || "0") * 100,
							) || null,
						available: v.availableForSale !== false,
						title: v.title,
						code: candidate,
					};
				});
		};
		return tryCandidate(0).catch(function (e) {
			log("variantBySelectedOptions failed", e);
			return null;
		});
	}

	// Last-resort placeholder from the capped Ajax list, used only when there is no
	// paint code or the code has no matching kit.
	function pickFallbackVariant(product) {
		if (!product || !product.variants || !product.variants.length) return null;
		if (CFG.paintRequiresSizeOption === false) {
			return (
				product.variants.find(function (v) {
					return v.available;
				}) ||
				product.variants[0] ||
				null
			);
		}
		var half = product.variants.filter(function (v) {
			var t = (v.title || "").toLowerCase();
			return t.indexOf("1/2") !== -1 || v.price === 1799;
		});
		var pool = half.length ? half : product.variants;
		return (
			pool.find(function (v) {
				return v.available;
			}) ||
			pool[0] ||
			null
		);
	}

	// Resolve the paint variant for the current code; updates state + UI.
	// Rapid-fire quality/paint changes (e.g. an FBT-driven PDP sync flipping
	// quality then paint then variant in quick succession) can start several of
	// these in flight at once; an earlier call's network round-trip may finish
	// AFTER a later one, so a monotonic token guards against a stale response
	// clobbering the freshest resolution (and incorrectly unchecking Touch-Up).
	var _paintResolveToken = 0;
	function resolvePaint() {
		var token = ++_paintResolveToken;
		state.paint.code = getCurrentPaintCode();
		if (!CFG.paintBottleHandle) {
			state.paint.available = false;
			state.paint.variantId = null;
			state.paint.resolved = true;
			updatePaintWarning();
			return Promise.resolve();
		}
		// Product fetch (image + fallback) and the exact-variant lookup run in parallel.
		var imgP = fetchBottleProduct().then(function (product) {
			if (token !== _paintResolveToken) return product;
			if (
				product &&
				(product.featured_image || (product.images && product.images[0]))
			) {
				state.paint.image = product.featured_image || product.images[0];
				updatePaintImage();
			}
			return product;
		});
		var codeP = state.paint.code
			? resolveVariantByCode(state.paint.code)
			: Promise.resolve(null);

		return Promise.all([imgP, codeP]).then(function (arr) {
			if (token !== _paintResolveToken) return; // superseded by a newer resolvePaint() call
			var product = arr[0],
				exact = arr[1];
			if (exact) {
				// Correct paint-code bottle resolved (right color).
				state.paint.variantId = exact.id;
				state.paint.priceCents = exact.priceCents || state.paint.priceCents;
				state.paint.available = exact.available !== false;
				state.paint.matched = true;
			} else {
				// No code, or the code has no kit for this make → placeholder + warning.
				// The line item still carries the requested Paint Code so fulfillment matches it.
				var fb = pickFallbackVariant(product);
				if (fb) {
					state.paint.variantId = fb.id;
					state.paint.priceCents = fb.price || state.paint.priceCents;
					state.paint.available = fb.available !== false;
				} else {
					state.paint.variantId = null;
					state.paint.available = false;
				}
				state.paint.matched = false;
			}
			state.paint.resolved = true;
			state.paint.resolvedForCode = state.paint.code || null;
			if (!state.paint.available && state.paint.selected) {
				state.paint.selected = false; // can't select a sold-out add-on
			}
			reflectToggles();
			updateAvailabilityUI();
			updatePaintWarning();
			updateTotals();
		});
	}

	// Ensure the paint variant matches the CURRENT on-page paint code. VIN auto-decode
	// can set the code without firing a change event, so we always re-check at add time.
	function ensurePaintResolved() {
		if (!state.paint.selected) return Promise.resolve();
		var code = getCurrentPaintCode();
		if (
			state.paint.resolved &&
			state.paint.resolvedForCode === code &&
			state.paint.variantId
		) {
			return Promise.resolve();
		}
		return resolvePaint();
	}

	function updatePaintImage() {
		if (!state.paint.image || !els.wrap) return;
		var img = els.wrap.querySelector('[data-item-img="paint"]');
		if (img) {
			var src = state.paint.image;
			// Shopify image URLs accept a size suffix for a lighter thumbnail.
			img.src = src.replace(/(\.(?:jpg|jpeg|png|webp))(\?|$)/i, "_160x160$1$2");
		}
	}

	// ── Render ───────────────────────────────────────────────────────────────
	function buildMarkup() {
		var kitName = CFG.kitName || "240-Piece Bumper Clip Kit";
		var kitImg = CFG.kitImage || "";
		var paintName = CFG.paintName || "Touch-Up Bottle Essential Kit";
		var paintImg = CFG.paintImage || "";
		var placeholderImg =
			"//partifyusa.com/cdn/shop/files/Partify_Photo_Coming_Soon.png?format=webp&v=1747228850&width=120";

		// Only render the cards that are eligible for this product.
		var cards = [];
		if (CLIP_ON) {
			cards.push(
				itemHTML(
					"clip",
					kitName,
					kitImg || placeholderImg,
					"240 OEM-grade clips &amp; fasteners. Every clip for a rattle-free install.",
					state.clip.priceCents,
				),
			);
		}
		if (PAINT_ON) {
			cards.push(
				itemHTML(
					"paint",
					paintName,
					paintImg || placeholderImg,
					"Essential touch-up paint kit matched to your selected paint.",
					state.paint.priceCents,
				),
			);
		}
		var itemsHtml = cards.join(
			'<div class="casper-bundle__plus" aria-hidden="true">+</div>',
		);

		var defaultCents =
			(CLIP_ON ? state.clip.priceCents : 0) +
			(PAINT_ON ? state.paint.priceCents : 0);

		var wrap = document.createElement("div");
		wrap.className = "casper-bundle";
		wrap.setAttribute("data-casper-bundle", "");
		wrap.innerHTML = [
			'<label class="casper-bundle__header" aria-label="Add recommended extras to your order">',
			'  <input type="checkbox" class="casper-bundle__master-input" data-master aria-label="Add all extras" />',
			'  <span class="casper-bundle__track" aria-hidden="true"><span class="casper-bundle__thumb"></span></span>',
			'  <span class="casper-bundle__head-text">',
			'    <span class="casper-bundle__title">Complete your repair &amp; save</span>',
			'    <span class="casper-bundle__subtitle">Everything you need for a clean, complete install.</span>',
			"  </span>",
			'  <span class="casper-bundle__head-price" data-master-price>+' +
				formatMoney(defaultCents) +
				"</span>",
			"</label>",
			'<div class="casper-bundle__items">',
			itemsHtml,
			"</div>",
			'<p class="casper-bundle__warning" data-paint-warning hidden></p>',
			'<div class="casper-bundle__total">',
			"  <span>Total with add-ons</span>",
			"  <span data-bundle-total>" +
				formatMoney(getBasePriceCents()) +
				"</span>",
			"</div>",
		].join("");
		return wrap;
	}

	function itemHTML(key, name, img, desc, priceCents) {
		return [
			'<div class="casper-bundle__item" data-item="' + key + '">',
			'  <label class="casper-bundle__item-toggle" aria-label="Add ' +
				name +
				'">',
			'    <input type="checkbox" class="casper-bundle__item-input" data-item-input="' +
				key +
				'" />',
			'    <span class="casper-bundle__mini-track"><span class="casper-bundle__mini-thumb"></span></span>',
			"  </label>",
			'  <div class="casper-bundle__item-media"><img data-item-img="' +
				key +
				'" src="' +
				img +
				'" alt="' +
				name +
				'" loading="lazy" /></div>',
			'  <div class="casper-bundle__item-name">' + name + "</div>",
			'  <div class="casper-bundle__item-desc">' + desc + "</div>",
			key === "paint"
				? '  <button class="casper-bundle__guide-button" type="button" data-touchup-guide-open aria-label="Open touch-up paint application guide" aria-haspopup="dialog" aria-controls="casper-touchup-guide" aria-expanded="false"><span aria-hidden="true">i</span></button>'
				: "",
			'  <div class="casper-bundle__item-price">+' +
				formatMoney(priceCents) +
				"</div>",
			'  <div class="casper-bundle__item-ship" data-item-ship="' +
				key +
				'">In stock &middot; Free shipping</div>',
			"</div>",
		].join("");
	}

	function touchupGuideMarkup() {
		return [
			'<dialog class="casper-bundle__guide-modal" id="casper-touchup-guide" data-touchup-guide-dialog aria-labelledby="casper-touchup-guide-title">',
			'  <div class="casper-bundle__guide-panel">',
			'    <div class="casper-bundle__guide-hero">',
			'      <button class="casper-bundle__guide-close" type="button" data-touchup-guide-close aria-label="Close application guide"><span aria-hidden="true">&times;</span></button>',
			'      <span class="casper-bundle__guide-eyebrow">Complete the repair</span>',
			'      <h2 id="casper-touchup-guide-title">The right finishing touch for your vehicle.</h2>',
			'      <p>Keep small chips and scratches from stealing the spotlight with a touch-up kit matched to your selected paint.</p>',
			'    </div>',
			'    <div class="casper-bundle__guide-content">',
			'      <p class="casper-bundle__guide-lead"><strong>One easy add-on. A more complete-looking repair.</strong> We use your selected paint information to send a kit made for the details that stand out most.</p>',
			'      <div class="casper-bundle__guide-benefits">',
			'        <div><span aria-hidden="true">01</span><strong>Matched to your paint</strong><p>Select a paint code above and we&rsquo;ll match the kit to that choice before it ships.</p></div>',
			'        <div><span aria-hidden="true">02</span><strong>Made for the small stuff</strong><p>Great for rock chips, short scratches, and the everyday imperfections worth fixing.</p></div>',
			'        <div><span aria-hidden="true">03</span><strong>Ready when you need it</strong><p>Add it with your part now so you have the right touch-up on hand after installation.</p></div>',
			'      </div>',
			'      <p class="casper-bundle__guide-note"><strong>Good to know:</strong> Application directions are included with your bottle.</p>',
			'    </div>',
			'  </div>',
			'</dialog>',
		].join("");
	}

	function getBasePriceCents() {
		var el = document.getElementById("price-display-a2c");
		var cents = el ? parseMoneyToCents(el.textContent) : null;
		if (cents != null) return cents;
		if (CFG.basePriceCents) return Number(CFG.basePriceCents);
		return 0;
	}

	// ── UI sync ───────────────────────────────────────────────────────────────
	function reflectToggles() {
		if (els.master) els.master.checked = masterOn();
		if (els.master) els.master.disabled = !hasSelectableItems();
		if (els.clipInput) els.clipInput.checked = state.clip.selected;
		if (els.paintInput) els.paintInput.checked = state.paint.selected;
		if (els.wrap)
			els.wrap.classList.toggle("casper-bundle--active", masterOn());
		var clipCard = els.wrap && els.wrap.querySelector('[data-item="clip"]');
		var paintCard = els.wrap && els.wrap.querySelector('[data-item="paint"]');
		if (clipCard) clipCard.classList.toggle("is-selected", state.clip.selected);
		if (paintCard)
			paintCard.classList.toggle("is-selected", state.paint.selected);
	}

	// The paint bottle is only selectable once an exact paint code is matched — you can't
	// add a bottle without knowing the color. (Clip kit needs no code.)
	function paintReady() {
		const vinReady =
			!isMatchPaintByVinCode(state.paint.code) || !!getActiveVin();
		return (
			state.paint.available !== false &&
			state.paint.matched === true &&
			!!state.paint.variantId &&
			vinReady
		);
	}

	// Reflect each item's selectability on its card: gray out + disable the toggle when it
	// can't be added, and swap the shipping line to a clear reason. Prevents the "toggle is
	// on but nothing adds" confusion.
	function updateAvailabilityUI() {
		if (!els.wrap) return;

		// Clip — gated purely on stock.
		(function () {
			var card = els.wrap.querySelector('[data-item="clip"]');
			var ship = els.wrap.querySelector('[data-item-ship="clip"]');
			var input = els.wrap.querySelector('[data-item-input="clip"]');
			if (!card) return;
			var inCart = state.clip.inCart === true;
			var avail = state.clip.available !== false && !inCart;
			card.classList.toggle("is-unavailable", !avail);
			if (input) input.disabled = !avail;
			if (ship) {
				ship.classList.remove("is-prompt", "is-soldout");
				if (inCart) {
					ship.textContent = "Already in cart";
					ship.classList.add("is-prompt");
				} else {
					ship.textContent = avail
						? "In stock · Free shipping"
						: "Currently sold out";
					ship.classList.toggle("is-soldout", !avail);
				}
			}
		})();

		// Paint — selectable only when an exact paint-code bottle is resolved.
		(function () {
			var card = els.wrap.querySelector('[data-item="paint"]');
			var ship = els.wrap.querySelector('[data-item-ship="paint"]');
			var input = els.wrap.querySelector('[data-item-input="paint"]');
			if (!card) return;
			var inCart = state.paint.inCart === true;
			var ready = !inCart && paintReady();
			card.classList.toggle("is-unavailable", !ready);
			if (input) input.disabled = !ready;
			// Can't be selected without a code → make sure it isn't.
			if (!ready && state.paint.selected) {
				state.paint.selected = false;
				if (input) input.checked = false;
			}
			if (ship) {
				ship.classList.remove("is-soldout", "is-prompt");
				if (inCart) {
					ship.textContent = "Already in cart";
					ship.classList.add("is-prompt");
				} else if (ready) {
					ship.textContent = "In stock · Free shipping";
				} else if (!state.paint.code) {
					ship.textContent = "Select a paint code to add";
					ship.classList.add("is-prompt");
				} else if (isMatchPaintByVinCode(state.paint.code) && !getActiveVin()) {
					ship.textContent = "Enter your VIN to add";
					ship.classList.add("is-prompt");
				} else if (state.paint.available === false) {
					ship.textContent = "Currently sold out";
					ship.classList.add("is-soldout");
				} else {
					ship.textContent = "No kit for code " + state.paint.code;
					ship.classList.add("is-soldout");
				}
			}
		})();

		// Re-sync master + total in case the paint was just auto-deselected above.
		if (els.master) els.master.checked = masterOn();
		if (els.master) els.master.disabled = !hasSelectableItems();
		var pc = els.wrap.querySelector('[data-item="paint"]');
		if (pc) pc.classList.toggle("is-selected", state.paint.selected);
		if (els.wrap)
			els.wrap.classList.toggle("casper-bundle--active", masterOn());
		updateTotals();
	}

	function updateClipImage() {
		if (!state.clip.image || !els.wrap) return;
		var img = els.wrap.querySelector('[data-item-img="clip"]');
		if (img)
			img.src = state.clip.image.replace(
				/(\.(?:jpg|jpeg|png|webp))(\?|$)/i,
				"_160x160$1$2",
			);
	}

	function updateTotals() {
		var total = getBasePriceCents();
		if (state.clip.selected) total += state.clip.priceCents;
		if (state.paint.selected) total += state.paint.priceCents;
		if (els.total) els.total.textContent = formatMoney(total);
		if (els.masterPrice) {
			// Header price = sum of selected add-ons, or (if none selected) the eligible ones.
			var addOns =
				(state.clip.selected ? state.clip.priceCents : 0) +
				(state.paint.selected ? state.paint.priceCents : 0);
			var eligibleSum =
				(clipVisible && !state.clip.inCart ? state.clip.priceCents : 0) +
				(paintVisible && !state.paint.inCart ? state.paint.priceCents : 0);
			els.masterPrice.textContent = "+" + formatMoney(addOns || eligibleSum);
		}
	}

	function updatePaintWarning() {
		if (!els.warning) return;
		// Show one simple note, and only while the touch-up paint card is rendered AND
		// greyed out (i.e. it can't be added because no painted code is selected). On
		// clip-only products (no bottle product for this store/make) there is no paint
		// card, so the note must never appear.
		var greyed = !paintReady();
		els.warning.textContent =
			isMatchPaintByVinCode(state.paint.code) && !getActiveVin()
				? "Enter your VIN above to add the touch-up paint kit."
				: "Touch-up paint kit is only available on painted parts — select a paint code above to add it.";
		els.warning.hidden = !(
			paintVisible &&
			greyed &&
			state.paint.inCart !== true
		);
	}

	// ── Event wiring ───────────────────────────────────────────────────────────
	function onMasterChange() {
		invalidateCartFilter();
		var turnOn = els.master.checked;
		// Only select what can actually be added: clip must be eligible (tagged) + in stock,
		// paint needs a matched code.
		state.clip.selected =
			turnOn &&
			clipVisible &&
			!state.clip.inCart &&
			state.clip.available !== false;
		state.paint.selected =
			turnOn && paintVisible && !state.paint.inCart && paintReady();
		reflectToggles();
		updatePaintWarning();
		updateTotals();
	}

	function onItemChange(key) {
		invalidateCartFilter();
		var checked = els[key + "Input"].checked;
		// Guard: clip can't be selected when sold out; paint can't be selected without a
		// resolved exact-match code (its toggle is disabled in that state anyway).
		var blocked =
			state[key].inCart === true ||
			(key === "paint" ? !paintReady() : state[key].available === false);
		if (checked && blocked) {
			els[key + "Input"].checked = false;
			state[key].selected = false;
			reflectToggles();
			return;
		}
		state[key].selected = checked;
		reflectToggles();
		updatePaintWarning();
		updateTotals();
	}

	function bindEvents() {
		if (els.master) els.master.addEventListener("change", onMasterChange);
		if (els.clipInput)
			els.clipInput.addEventListener("change", function () {
				onItemChange("clip");
			});
		if (els.paintInput)
			els.paintInput.addEventListener("change", function () {
				onItemChange("paint");
			});

		// Paint code can change via the paint dropdown / radios / variant updates.
		document.addEventListener("change", function (e) {
			if (!e.target) return;
			if (e.target.name === "properties[Bumper Material]") {
				applyCartFilter(state.clip.inCart, state.paint.inCart);
				return;
			}
			if (
				e.target.id === "variant-selector" ||
				e.target.name === "select_paint_option" ||
				e.target.name === "quality_type"
			) {
				// Always re-resolve so the paint card's enabled/disabled state tracks the
				// current code — even when the paint toggle isn't selected (e.g. switching
				// to Unpainted must immediately disable the paint add-on).
				state.paint.resolved = false;
				resolvePaint();
			}
		});
		document.addEventListener("variantsUpdated", function () {
			state.paint.resolved = false;
			resolvePaint();
		});

		// Keep our self-contained total in sync with the live base price.
		var priceEl = document.getElementById("price-display-a2c");
		if (priceEl && window.MutationObserver) {
			new MutationObserver(updateTotals).observe(priceEl, {
				childList: true,
				characterData: true,
				subtree: true,
			});
		}
		if (els.guideOpen && els.guideDialog) {
			els.guideOpen.addEventListener("click", function () {
				els.guideDialog.showModal();
				els.guideOpen.setAttribute("aria-expanded", "true");
			});
		}
		if (els.guideClose && els.guideDialog) {
			els.guideClose.addEventListener("click", function () {
				els.guideDialog.close();
			});
		}
		if (els.guideDialog && els.guideOpen) {
			els.guideDialog.addEventListener("close", function () {
				els.guideOpen.setAttribute("aria-expanded", "false");
				els.guideOpen.focus();
			});
		}
	}

	// ── Add to Cart intercept (CA-proven capture pattern) ────────────────────
	var _resuming = false;
	var _adding = false;

	function findAtcButtons() {
		var sels = [".add-to-cart", ".sticky-add-to-cart"];
		var out = [];
		sels.forEach(function (s) {
			document.querySelectorAll(s).forEach(function (b) {
				out.push(b);
			});
		});
		return out;
	}

	function getProductForm() {
		return els.wrap && els.wrap.closest("form#product-form");
	}

	function buildAddOnItems() {
		var items = [];
		var shared = { Bundled: "Casper add-on bundle" };
		var activeVin = getActiveVin();
		if (activeVin) shared.VIN = activeVin;
		if (
			clipVisible &&
			!state.clip.inCart &&
			state.clip.selected &&
			state.clip.variantId &&
			state.clip.available !== false
		) {
			items.push({
				id: String(state.clip.variantId),
				quantity: 1,
				properties: Object.assign({}, shared),
			});
		}
		// Only add the paint bottle when the EXACT paint code is matched — never a wrong
		// color. If the code can't be resolved to a real bottle variant, skip it (the
		// warning explains why) rather than adding a placeholder.
		if (
			paintVisible &&
			!state.paint.inCart &&
			state.paint.selected &&
			paintReady()
		) {
			items.push({
				id: String(state.paint.variantId),
				quantity: 1,
				properties: getPaintLineProperties(),
			});
		}
		return items;
	}

	function findSelectedAddOnDuplicates(cartItems, items) {
		const selectedVariantIds = items.map(function (item) {
			return String(item.id);
		});
		const duplicates = [];

		if (
			state.clip.variantId &&
			selectedVariantIds.indexOf(String(state.clip.variantId)) !== -1
		) {
			const clipDuplicate = cartItems.find(function (item) {
				return (
					(CFG.kitProductHandle && item.handle === CFG.kitProductHandle) ||
					String(item.variant_id) === String(state.clip.variantId)
				);
			});
			if (clipDuplicate) duplicates.push(clipDuplicate);
		}

		if (
			state.paint.variantId &&
			selectedVariantIds.indexOf(String(state.paint.variantId)) !== -1
		) {
			const selectedPaintItem = items.find(function (item) {
				return String(item.id) === String(state.paint.variantId);
			});
			const paintDuplicate = cartItems.find(function (item) {
				return samePaintConfiguration(item, selectedPaintItem);
			});
			if (paintDuplicate && duplicates.indexOf(paintDuplicate) === -1)
				duplicates.push(paintDuplicate);
		}

		return duplicates;
	}

	function confirmSelectedAddOnDuplicates(items) {
		if (
			!items.length ||
			typeof window.__partifyConfirmCartDuplicate !== "function"
		) {
			return Promise.resolve(true);
		}

		return fetch("/cart.js", {
			cache: "reload",
			credentials: "same-origin",
			headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
		})
			.then(function (response) {
				return response.ok ? response.json() : null;
			})
			.then(function (cart) {
				if (!cart || !Array.isArray(cart.items)) return true;
				const guardItems = items.map(function (item) {
					const isClip = state.clip.variantId && String(item.id) === String(state.clip.variantId);
					return Object.assign({}, item, {
						variant_id: item.id,
						productHandle: isClip ? CFG.kitProductHandle : CFG.paintBottleHandle,
						productTitle: isClip ? CFG.kitName : CFG.paintName,
						title: isClip ? CFG.kitName : CFG.paintName,
						partifyQuantityRuleKey: isClip ? "bumper_clip_kit" : "touch_up_paint_kit",
					});
				});
				if (
					window.PartifyCartQuantityConfirmation &&
					typeof window.PartifyCartQuantityConfirmation.confirmAdd === "function"
				) {
					return window.PartifyCartQuantityConfirmation.confirmAdd({
						cart: cart,
						items: guardItems,
					}).then(function (result) {
						if (!result.confirmed) return false;
						if (result.matched) return true;
						const duplicates = findSelectedAddOnDuplicates(cart.items, items);
						return duplicates.reduce(function (confirmation, duplicateItem) {
							return confirmation.then(function (confirmed) {
								return confirmed ? window.__partifyConfirmCartDuplicate(duplicateItem) : false;
							});
						}, Promise.resolve(true));
					});
				}
				const duplicates = findSelectedAddOnDuplicates(cart.items, items);
				return duplicates.reduce(function (confirmation, duplicateItem) {
					return confirmation.then(function (confirmed) {
						return confirmed
							? window.__partifyConfirmCartDuplicate(duplicateItem)
							: false;
					});
				}, Promise.resolve(true));
			})
			.catch(function () {
				return true;
			});
	}

	window.__partifyGetSelectedCasperAddOns = function () {
		// Preserve the confirmed selection while paint resolution is awaiting a response.
		invalidateCartFilter();
		return ensurePaintResolved().then(collectAddOnItems);
	};

	// Read-only summary for the desktop optional-extras subtotal.
	window.__partifyGetCasperPaintLineProperties = getPaintLineProperties;
	window.__partifyGetAddOnStyleCasperSummary = function () {
		const code = getCurrentPaintCode();
		const exactPaintReady = paintVisible && !!code && !isMatchPaintByVinCode(code) &&
			state.paint.resolvedForCode === code && paintReady() && !state.paint.inCart;
		return {
			clip: { ready: !!(clipVisible && state.clip.variantId && state.clip.available !== false && !state.clip.inCart), selected: state.clip.selected, priceCents: state.clip.priceCents, variantId: state.clip.variantId, image: state.clip.image, handle: CFG.kitProductHandle },
			paint: { ready: !!exactPaintReady, selected: state.paint.selected, priceCents: state.paint.priceCents, variantId: state.paint.variantId, image: state.paint.image, handle: CFG.paintBottleHandle, code: exactPaintReady ? code : null },
			basePriceCents: getBasePriceCents()
		};
	};

	if (window.__PARTIFY_DESKTOP_ADDON_SPLIT) window.__partifyGetCasperAddOnSummary = window.__partifyGetAddOnStyleCasperSummary;

	async function collectAddOnItems() {
		const reportItems = typeof window.__partifyGetSelectedVehicleHistoryAddOn === "function"
			? await window.__partifyGetSelectedVehicleHistoryAddOn() : [];
		return buildAddOnItems().concat(reportItems);
	}

	function reportSelected() {
		return typeof window.__partifyVehicleHistoryAddOnSelected === "function" && window.__partifyVehicleHistoryAddOnSelected();
	}

	// Minimal hooks for callers (e.g. the FBT "This item" paint/quality sync)
	// that change the main PDP's paint/quality out from under Casper and need
	// to preserve "Touch-Up Paint was selected" intent across that change.
	window.__partifyCasperTouchUpIsSelected = function () {
		return !!(paintVisible && state.paint.selected);
	};

	window.__partifyCasperRestoreTouchUpSelection = function () {
		if (!paintVisible) return Promise.resolve(false);
		invalidateCartFilter();
		state.paint.selected = true;
		state.paint.resolved = false;
		return resolvePaint().then(function () {
			return !!state.paint.selected;
		});
	};

	function addOne(item) {
		return fetch("/cart/add.js", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Accept: "application/json",
			},
			body: JSON.stringify(item),
		}).then(function (res) {
			return res.json().then(function (body) {
				return { ok: res.ok, status: res.status, body: body, item: item };
			});
		});
	}

	function commitSelectedAddOns() {
		invalidateCartFilter();
		return ensurePaintResolved().then(collectAddOnItems).then(function (items) {
			if (!items.length) {
				updatePaintWarning();
				return true;
			}
			return confirmSelectedAddOnDuplicates(items.filter(item => item.properties?._vhr_schema !== "1")).then(function (confirmed) {
				if (!confirmed) return false;
				if (typeof window.__partifyValidateVehicleHistoryAddOn === "function") window.__partifyValidateVehicleHistoryAddOn(items);
				return Promise.all(
					items.map(function (it) {
						return addOne(it).catch(function (err) {
							return { ok: false, error: String(err), item: it };
						});
					}),
				).then(function (results) {
					const failed = results.filter(function (r) {
						return !r.ok;
					});
					failed.forEach(function (r) {
						log(
							"add-on failed",
							r.status,
							(r.body && r.body.message) || r.error,
						);
					});
					if (failed.length) {
						if (failed.some(function (result) { return result.item.properties?._vhr_schema === "1"; })) {
							showAddNote(CFG.vehicleHistory.strings.retry);
							return false;
						}
						const soldOut = failed.map(function (r) {
							return String(r.variantId || r.item.id) ===
								String(state.clip.variantId)
								? "the bumper clip kit"
								: "the touch-up paint kit";
						});
						showAddNote(
							"We couldn't add " +
								soldOut.join(" and ") +
								" (currently unavailable). The rest of your order was added.",
						);
					}
					return true;
				});
			});
		});
	}

	window.__partifyCommitSelectedCasperAddOns = function () {
		return commitSelectedAddOns().catch(function (err) {
			log("add-on commit failed", err);
			if (reportSelected()) {
				showAddNote(err.message || CFG.vehicleHistory.strings.retry);
				return false;
			}
			return true;
		});
	};

	function shouldDeferAddOnsForAssemblyGate() {
		const bundleWrap = document.getElementById("bundle-options");
		return !!(
			bundleWrap &&
			bundleWrap.style.display !== "none" &&
			!bundleWrap.hasAttribute("data-kit-addon-replaced") &&
			!document.querySelector('input[name="bundle_mode"]:checked')
		);
	}

	function fitmentGateWillBlock() {
		return !!(
			window.__pdpFitmentGate &&
			typeof window.__pdpFitmentGateIsReady === "function" &&
			!window.__pdpFitmentGateIsReady()
		);
	}

	function onAtcClick(e) {
		if (_adding && !_resuming) {
			e.preventDefault();
			e.stopImmediatePropagation();
			return;
		}
		if (_resuming) {
			_resuming = false;
			return; // let the theme's own handler add the main product
		}
		const btn = e.currentTarget;
		const form = btn && (btn.form || btn.closest("form"));
		if (form && form.getAttribute("id") === "product-form") return;
		if (!masterOn()) return; // no add-ons selected → normal flow

		// Don't orphan our add-ons: if the Assembly Options gate is visible but no
		// assembly mode is chosen, kit-bundle.liquid will block the main add. Defer
		// to it (no preventDefault) so nothing is added until assembly is selected.
		if (shouldDeferAddOnsForAssemblyGate()) {
			return;
		}

		// If the fitment gate will block the ensuing form submit anyway, stand down.
		// Add-ons will be committed on the next click, after the user has both a
		// vehicle (from the modal) and a paint selection — preventing premature
		// cart commits that cause duplicate add-on line items.
		if (fitmentGateWillBlock()) {
			return;
		}

		// Take over the click; we'll resume the native add after our add-ons land.
		e.preventDefault();
		e.stopImmediatePropagation();
		_adding = true;
		btn.setAttribute("aria-busy", "true");

		// Re-resolve the paint for the current code FIRST (handles VIN auto-decode that
		// set the code with no change event), then add each add-on as a SEPARATE request
		// so one sold-out item (HTTP 422) can never block the others.
		commitSelectedAddOns()
			.catch(function (err) {
				log("add-on commit failed", err);
				return true;
			})
			.then(function (shouldResumeMain) {
				btn.removeAttribute("aria-busy");
				if (!shouldResumeMain) {
					_adding = false;
					return;
				}
				// Resume the native add of the main product. Listener passes through via _resuming.
				_resuming = true;
				btn.click();
				setTimeout(function () {
					_adding = false;
				}, 3000);
			});
	}

	function showAddNote(msg) {
		if (!els.warning) return;
		els.warning.textContent = msg;
		els.warning.hidden = false;
	}

	function attachAtc() {
		findAtcButtons().forEach(function (btn) {
			if (btn.__casperBound) return;
			btn.__casperBound = true;
			btn.addEventListener("click", onAtcClick, true);
		});
		window.addEventListener("pageshow", function (event) {
			const navigationEntry =
				performance.getEntriesByType &&
				performance.getEntriesByType("navigation")[0];
			if (
				!event.persisted &&
				(!navigationEntry || navigationEntry.type !== "back_forward")
			)
				return;
			_adding = false;
			_resuming = false;
			findAtcButtons().forEach(function (btn) {
				btn.removeAttribute("aria-busy");
			});
		});
	}

	// ── Mount ────────────────────────────────────────────────────────────────
	function injectStyles() {
		if (document.getElementById("casper-bundle-styles")) return;
		var s = document.createElement("style");
		s.id = "casper-bundle-styles";
		s.textContent = STYLES;
		document.head.appendChild(s);
	}

	function mount() {
		if (document.querySelector("[data-casper-bundle]")) return;
		injectStyles();
		var anchor = document.getElementById("bundle-options");
		var wrap = buildMarkup();
		if (PAINT_ON) wrap.insertAdjacentHTML("beforeend", touchupGuideMarkup());

		if (anchor && anchor.parentNode) {
			anchor.parentNode.insertBefore(wrap, anchor.nextSibling);
		} else {
			var form = document.querySelector('form[action*="/cart/add"]');
			var atcWrap = document.querySelector(".add-to-cart-button-wrapper");
			if (atcWrap && atcWrap.parentNode) {
				atcWrap.parentNode.insertBefore(wrap, atcWrap);
			} else if (form) {
				form.appendChild(wrap);
			} else {
				log("no mount anchor found — aborting");
				return;
			}
		}

		els.wrap = wrap;
		els.master = wrap.querySelector("[data-master]");
		els.masterPrice = wrap.querySelector("[data-master-price]");
		els.clipInput = wrap.querySelector('[data-item-input="clip"]');
		els.paintInput = wrap.querySelector('[data-item-input="paint"]');
		els.warning = wrap.querySelector("[data-paint-warning]");
		els.total = wrap.querySelector("[data-bundle-total]");
		els.guideOpen = wrap.querySelector("[data-touchup-guide-open]");
		els.guideDialog = wrap.querySelector("[data-touchup-guide-dialog]");
		els.guideClose = wrap.querySelector("[data-touchup-guide-close]");

		// Fetch the kit product to confirm stock + image + price — only when the clip is
		// eligible for this bumper (tagged 'Bundle-Bumper-Clips'); otherwise there's no
		// clip card to populate.
		if (CLIP_ON && CFG.kitProductHandle) {
			fetch("/products/" + CFG.kitProductHandle + ".js")
				.then(function (r) {
					return r.ok ? r.json() : null;
				})
				.then(function (p) {
					if (!p || !p.variants || !p.variants[0]) return;
					var v = p.variants[0];
					if (!state.clip.variantId) state.clip.variantId = v.id;
					state.clip.priceCents = v.price || state.clip.priceCents;
					state.clip.available = v.available !== false;
					if (p.featured_image) {
						state.clip.image = p.featured_image;
						updateClipImage();
					}
					if (!state.clip.available && state.clip.selected)
						state.clip.selected = false;
					reflectToggles();
					updateAvailabilityUI();
					updateTotals();
				})
				.catch(function () {});
		}

		// Pre-read any paint code already chosen (auto-copy on load).
		state.paint.code = getCurrentPaintCode();

		bindEvents();
		bindCartChangeListener();
		attachAtc();
		reflectToggles();
		updateAvailabilityUI();
		updateTotals();
		updatePaintWarning();

		// Paint-only setup (skip entirely when the paint card isn't shown).
		if (PAINT_ON) {
			// Resolve the touch-up paint kit on load so the card shows the real photo,
			// price and availability even before the customer toggles it on.
			resolvePaint();

			// VIN auto-decode sets the paint code WITHOUT firing a change event, so a single
			// mount-time resolve can miss it. Poll briefly until the code appears/stabilizes and
			// re-resolve, so the displayed match (and the add) reflect the right code every time.
			var _lastSeenCode = state.paint.code;
			var _codePolls = 0;
			var _codePoll = setInterval(function () {
				_codePolls++;
				var c = getCurrentPaintCode();
				if (c !== _lastSeenCode) {
					_lastSeenCode = c;
					state.paint.resolved = false;
					resolvePaint();
				}
				if (_codePolls >= 20) clearInterval(_codePoll); // ~10s safety cap
			}, 500);
		}
		refreshCartFilter(invalidateCartFilter());

		log("mounted", {
			kitVariant: state.clip.variantId,
			paintBottle: CFG.paintBottleHandle,
		});
	}

	// ── Styles (Casper-style dual card; ported palette from main-ca widget) ───
	var STYLES = [
		// Card shell
		".casper-bundle{box-sizing:border-box;background:#fff;border:1px solid #e3e8ef;border-radius:16px;margin:18px 0;width:100%;font-family:inherit;overflow:hidden;box-shadow:0 1px 3px rgba(16,24,40,.06);transition:border-color .2s,box-shadow .2s}",
		".casper-bundle *,.casper-bundle *::before,.casper-bundle *::after{box-sizing:border-box}",
		".casper-bundle--active{border-color:#1c3fa8;box-shadow:0 4px 18px rgba(28,63,168,.13)}",
		// Header (master toggle row)
		".casper-bundle__header{display:flex;align-items:center;gap:13px;padding:15px 18px;cursor:pointer;margin:0;background:#f8fafc;border-bottom:1px solid #eef0f4;-webkit-user-select:none;user-select:none}",
		".casper-bundle--active .casper-bundle__header{background:#eff4ff;border-bottom-color:#dbe5fb}",
		".casper-bundle__master-input,.casper-bundle__item-input{position:absolute;opacity:0;width:0;height:0;margin:0}",
		".casper-bundle__track{width:46px;height:26px;background:#cbd2dd;border-radius:999px;position:relative;flex-shrink:0;transition:background .2s}",
		".casper-bundle__thumb{width:20px;height:20px;background:#fff;border-radius:50%;position:absolute;top:3px;left:3px;transition:transform .2s;box-shadow:0 1px 3px rgba(16,24,40,.3)}",
		".casper-bundle--active .casper-bundle__track{background:#1c3fa8}",
		".casper-bundle--active .casper-bundle__thumb{transform:translateX(20px)}",
		".casper-bundle__head-text{flex:1;display:flex;flex-direction:column;gap:1px;min-width:0}",
		".casper-bundle__title{font-size:15px;font-weight:700;color:#101828;line-height:1.3;letter-spacing:-.01em}",
		".casper-bundle__subtitle{font-size:12px;color:#667085;line-height:1.4}",
		".casper-bundle__head-price{font-size:16px;font-weight:700;color:#1c3fa8;white-space:nowrap;letter-spacing:-.01em}",
		// Items row
		".casper-bundle__items{display:flex;align-items:stretch;justify-content:center;gap:10px;padding:14px}",
		".casper-bundle__item{position:relative;flex:1 1 0;min-width:0;display:flex;flex-direction:column;align-items:center;text-align:center;gap:5px;padding:18px 14px 15px;border:1px solid #eaecf0;border-radius:12px;background:#fff;transition:border-color .18s,background .18s,box-shadow .18s}",
		".casper-bundle__item[hidden]{display:none}",
		".casper-bundle__item:hover{border-color:#d3dbe8}",
		".casper-bundle__item.is-selected{background:#f7f9ff;border-color:#1c3fa8;box-shadow:0 0 0 1px #1c3fa8 inset}",
		".casper-bundle__item-media{width:84px;height:84px;display:flex;align-items:center;justify-content:center;background:#f9fafb;border:1px solid #f0f1f3;border-radius:10px;padding:7px;margin-bottom:2px}",
		".casper-bundle__item-media img{max-width:100%;max-height:100%;width:auto;height:auto;object-fit:contain;mix-blend-mode:multiply}",
		".casper-bundle__item-name{font-size:13px;font-weight:600;color:#1c3fa8;line-height:1.35;letter-spacing:-.01em}",
		".casper-bundle__item-desc{font-size:11.5px;color:#667085;line-height:1.45;max-width:24ch}",
		".casper-bundle__guide-button{display:inline-flex;align-items:center;justify-content:center;width:24px;height:24px;margin:1px 0 0;padding:0;border:1px solid #cbd5e1;border-radius:50%;background:#fff;color:#344054;font:600 13px/1 Arial,sans-serif;cursor:pointer;transition:border-color .18s,background .18s,color .18s,transform .18s}",
		".casper-bundle__guide-button:hover{border-color:#1c3fa8;background:#eff4ff;color:#1c3fa8;transform:scale(1.04)}",
		".casper-bundle__guide-button:focus-visible,.casper-bundle__guide-close:focus-visible{outline:3px solid #f4c542;outline-offset:3px}",
		".casper-bundle__guide-modal{width:min(680px,calc(100vw - 32px));max-height:calc(100vh - 32px);margin:auto;padding:0;border:0;border-radius:24px;background:#fff;color:#101828;box-shadow:0 28px 90px rgba(16,24,40,.34)}",
		".casper-bundle__guide-modal::backdrop{background:rgba(15,23,42,.48);backdrop-filter:blur(4px)}",
		".casper-bundle__guide-panel{overflow:auto;max-height:calc(100vh - 32px)}",
		".casper-bundle__guide-hero{position:relative;padding:35px 42px 32px;background:linear-gradient(135deg,#102d8f 0%,#1c3fa8 52%,#345ece 100%);color:#fff;text-align:left}",
		".casper-bundle__guide-eyebrow{display:block;margin-bottom:9px;color:#bcd0ff;font-size:11px;font-weight:800;letter-spacing:.11em;text-transform:uppercase}",
		".casper-bundle__guide-hero h2{max-width:13ch;margin:0;color:#fff;font-size:32px;line-height:1.04;letter-spacing:-.045em}",
		".casper-bundle__guide-hero p{max-width:48ch;margin:14px 0 0;color:#e3ebff;font-size:15px;line-height:1.5}",
		".casper-bundle__guide-close{position:absolute;top:19px;right:19px;display:inline-flex;align-items:center;justify-content:center;width:34px;height:34px;padding:0;border:1px solid rgba(255,255,255,.2);border-radius:50%;background:rgba(255,255,255,.12);color:#fff;font-size:26px;line-height:1;cursor:pointer;transition:background .18s,transform .18s}",
		".casper-bundle__guide-close:hover{background:rgba(255,255,255,.23);transform:scale(1.05)}",
		".casper-bundle__guide-content{padding:29px 42px 34px}",
		".casper-bundle__guide-lead{max-width:54ch;margin:0 0 23px;color:#475467;font-size:15px;line-height:1.55;text-align:left}",
		".casper-bundle__guide-lead strong{color:#101828}",
		".casper-bundle__guide-benefits{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px;text-align:left}",
		".casper-bundle__guide-benefits>div{min-height:164px;padding:17px 15px;border:1px solid #e2e8f5;border-radius:14px;background:#f8faff}",
		".casper-bundle__guide-benefits span{display:block;margin-bottom:18px;color:#1c3fa8;font-size:10px;font-weight:800;letter-spacing:.09em}",
		".casper-bundle__guide-benefits strong{display:block;margin-bottom:7px;color:#101828;font-size:14px;line-height:1.25}",
		".casper-bundle__guide-benefits p{margin:0;color:#667085;font-size:12px;line-height:1.48}",
		".casper-bundle__guide-note{margin:18px 0 0;padding:12px 14px;border-left:3px solid #f4c542;border-radius:8px;background:#fffbeb;color:#5f4b13;font-size:12px;line-height:1.45;text-align:left}",
		".casper-bundle__item-price{font-size:14px;font-weight:700;color:#101828;margin-top:3px;letter-spacing:-.01em}",
		".casper-bundle__item-ship{display:inline-flex;align-items:center;gap:5px;font-size:11px;color:#0f7a52;font-weight:600}",
		".casper-bundle__item-ship::before{content:'';width:6px;height:6px;border-radius:50%;background:#16a571;flex-shrink:0}",
		".casper-bundle__item-ship.is-soldout{color:#b42318}",
		".casper-bundle__item-ship.is-soldout::before{background:#d92d20}",
		".casper-bundle__item-ship.is-prompt{color:#8a6d1b}",
		".casper-bundle__item-ship.is-prompt::before{background:#d9a514}",
		".casper-bundle__item.is-unavailable{opacity:.55}",
		".casper-bundle__item.is-unavailable .casper-bundle__item-toggle{opacity:.4;cursor:not-allowed;pointer-events:none}",
		// Per-item mini toggle (top-right of each item)
		".casper-bundle__item-toggle{position:absolute;top:11px;right:11px;cursor:pointer;margin:0;z-index:1}",
		".casper-bundle__mini-track{display:block;width:36px;height:21px;background:#cbd2dd;border-radius:999px;position:relative;transition:background .2s}",
		".casper-bundle__mini-thumb{width:15px;height:15px;background:#fff;border-radius:50%;position:absolute;top:3px;left:3px;transition:transform .2s;box-shadow:0 1px 2px rgba(16,24,40,.3)}",
		".casper-bundle__item.is-selected .casper-bundle__mini-track{background:#1c3fa8}",
		".casper-bundle__item.is-selected .casper-bundle__mini-thumb{transform:translateX(15px)}",
		// "+" divider
		".casper-bundle__plus{flex:0 0 auto;align-self:center;display:flex;align-items:center;justify-content:center;width:26px;height:26px;border-radius:50%;background:#fff;border:1px solid #e3e8ef;color:#98a2b3;font-size:15px;font-weight:400;line-height:1;box-shadow:0 1px 2px rgba(16,24,40,.06)}",
		// Warning
		".casper-bundle__warning{margin:0;padding:11px 18px;font-size:12px;color:#8a6d1b;background:#fffaf0;border-top:1px solid #f5e6c0;line-height:1.45}",
		// Total
		".casper-bundle__total{display:flex;justify-content:space-between;align-items:center;margin:0;padding:14px 18px;border-top:1px solid #eef0f4;font-size:14px;color:#475467;font-weight:500}",
		".casper-bundle__total span:last-child{font-weight:700;color:#101828;font-size:16px;letter-spacing:-.01em}",
		// Mobile
		"@media (max-width:600px){.casper-bundle__items{flex-direction:column;gap:8px}.casper-bundle__plus{margin:0 auto}.casper-bundle__guide-hero{padding:29px 24px 27px}.casper-bundle__guide-hero h2{font-size:27px}.casper-bundle__guide-content{padding:23px 20px 25px}.casper-bundle__guide-benefits{grid-template-columns:1fr}.casper-bundle__guide-benefits>div{min-height:0}.casper-bundle__guide-benefits span{margin-bottom:9px}}",
	].join("");

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", mount);
	} else {
		mount();
	}
})();
